import { Autocomplete, AutocompleteProps, Button, TextField } from "@mui/material";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { GqlProjectStatus } from "~/__generated__";
import useDebouncedState from "~/shared/hooks/useDebouncedState";

import useProjectSelect_GetAutocompleteOptions from "./api/useProjectSelect_GetAutocompleteOptions";

type Option = { label: string; value: string };

type AutocompleteProps_<Multiple extends boolean> = Omit<
    AutocompleteProps<Option, Multiple, false, false>,
    "onChange" | "options" | "renderInput" | "value"
>;

type BaseProjectSelectProps = {
    clientIds?: string[];
    helperText?: React.ReactNode;
    label: string;
    statuses?: GqlProjectStatus[];
};

export type ProjectSingleSelectProps = BaseProjectSelectProps &
    AutocompleteProps_<false> & {
        multiple?: false;
        onChange: (value: string | null) => void;
        value?: string | null;
    };

export type ProjectMultiSelectProps = BaseProjectSelectProps &
    AutocompleteProps_<true> & {
        allowSelectAll?: boolean;
        multiple: true;
        onChange: (value: string[]) => void;
        value?: string[] | null;
    };

export type ProjectSelectProps = ProjectSingleSelectProps | ProjectMultiSelectProps;

function ProjectSelect(props: ProjectSingleSelectProps): React.JSX.Element;
function ProjectSelect(props: ProjectMultiSelectProps): React.JSX.Element;
function ProjectSelect(props: ProjectSelectProps) {
    const { t } = useTranslation("shared");
    const isMulti = props.multiple === true;

    const {
        clientIds,
        helperText,
        label,
        onChange,
        size = "medium",
        value,
        // @ts-expect-error: `allowSelectAll` is not a valid prop for single select elements
        allowSelectAll,
        statuses,
        ...restProps
    } = props;

    const normalizedValue = Array.isArray(value) ? value : typeof value === "string" ? [value] : [];
    const [searchQuery, debouncedSearchQuery, setSearchQuery] = useDebouncedState("");

    const { options, selectedOptions, scrubbedSelectedIds, selectedIdsIsScrubbed, loading } =
        useProjectSelect_GetAutocompleteOptions({
            clientIds: clientIds,
            statuses: statuses,
            q: debouncedSearchQuery,
            selectedIds: normalizedValue,
        });

    useEffect(() => {
        if (!selectedIdsIsScrubbed) return;
        const nextValue = isMulti ? scrubbedSelectedIds : (scrubbedSelectedIds[0] ?? null);
        // @ts-expect-error: `onChange` is a union of a single- and a multi-select handler
        onChange(nextValue);
    }, [isMulti, onChange, scrubbedSelectedIds, selectedIdsIsScrubbed]);

    const handleSelectAllClick: React.MouseEventHandler = event => {
        if (!isMulti || options === undefined) return;

        // @ts-expect-error: `onChange` expects an array here
        onChange(options.map(x => x.value));
        props.onClose?.(event, "selectOption");
    };

    const selectedValue = useMemo(() => {
        if (isMulti) {
            const currentValue = (value as string[] | null) ?? [];
            return (selectedOptions ?? []).filter(o => currentValue.includes(o.value));
        }

        if (typeof value !== "string") {
            return undefined;
        }

        return (selectedOptions ?? []).find(o => o.value === value) ?? undefined;
    }, [selectedOptions, value, isMulti]);

    const helperText_ = (
        <>
            {isMulti && allowSelectAll && (
                <Button onClick={handleSelectAllClick} variant="text" size="small">
                    {t("ProjectSelectElement.selectAll")}
                </Button>
            )}
            {helperText}
        </>
    );

    const handleChange = (_: unknown, newValue: Option | Option[] | null) => {
        if (isMulti) {
            // @ts-expect-error: cannot runtime assert value
            onChange(newValue?.map(x => x.value) ?? []);
        } else {
            // @ts-expect-error: cannot runtime assert value
            onChange(newValue?.value ?? null);
        }
    };

    return (
        <Autocomplete
            {...(restProps as AutocompleteProps<Option, boolean, false, false>)}
            multiple={isMulti}
            options={options ?? []}
            isOptionEqualToValue={(option, selected) => option.value === selected.value}
            loading={loading}
            getOptionKey={option => option.value}
            getOptionLabel={option => option.label}
            value={selectedValue as Option | Option[] | undefined}
            onChange={handleChange}
            inputValue={searchQuery}
            onInputChange={(_, newInputValue, reason) => {
                if ("reset" === reason) return;
                setSearchQuery(newInputValue);
            }}
            renderInput={params => (
                <TextField {...params} helperText={helperText_} fullWidth variant="outlined" label={label} size={size} />
            )}
        />
    );
}

export default ProjectSelect;
